import React from 'react';
import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  GraduationCap,
  PlayCircle,
} from 'lucide-react';
import { CourseDetails, Lesson, Unit } from '../../hooks/useCourseApi';

export interface EnrolledCourseItem {
  id: string;
  courseName: string;
  term: string;
  grade: string;
  description: string;
  imagePath: string;
  progress?: number;
}

interface EnrolledCourseCardProps {
  course: EnrolledCourseItem;
  imageSrc: string;
  curriculum?: CourseDetails;
  isExpanded: boolean;
  isLoadingCurriculum: boolean;
  expandedUnitIndexes: Set<number>;
  onToggleCurriculum: () => void;
  onToggleUnit: (unitIndex: number) => void;
  onContinue: () => void;
  getLessonTypeIcon: (lesson: Lesson) => React.ReactNode;
}

const getTermLabel = (term: string) =>
  term === 'First' ? 'الترم الأول' : term === 'Second' ? 'الترم الثاني' : term;

const getGradeLabel = (grade: string) => {
  const labels: Record<string, string> = {
    Secondary1: 'أولى ثانوي',
    Secondary2: 'ثانية ثانوي',
    Secondary3: 'ثالثة ثانوي',
  };
  return labels[grade] || grade;
};

const EnrolledCourseCard: React.FC<EnrolledCourseCardProps> = ({
  course,
  imageSrc,
  curriculum,
  isExpanded,
  isLoadingCurriculum,
  expandedUnitIndexes,
  onToggleCurriculum,
  onToggleUnit,
  onContinue,
  getLessonTypeIcon,
}) => {
  const progress = course.progress ?? 0;

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-gold-400/40 hover:shadow-lg hover:shadow-[#001848]/8">
      <div className="h-1 w-full bg-gradient-to-l from-gold-400 via-gold-500 to-[#001848]" />

      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:gap-5 sm:p-5">
        <div className="relative h-36 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:h-32 sm:w-40">
          <img
            src={imageSrc}
            alt={course.courseName}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = '/default-course.jpg';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#001848]/45 to-transparent" />
          <div className="absolute start-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gold-500 shadow">
            <CheckCircle2 className="h-3.5 w-3.5 text-white" />
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
          <div>
            <h3 className="mb-2 line-clamp-2 text-lg font-bold leading-snug text-[#001848] font-cairo-bold sm:text-xl">
              {course.courseName}
            </h3>
            <div className="flex flex-wrap gap-2">
              {course.term && (
                <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 sm:text-xs">
                  {getTermLabel(course.term)}
                </span>
              )}
              {course.grade && (
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-[#001848] sm:text-xs">
                  <GraduationCap className="h-3.5 w-3.5" />
                  {getGradeLabel(course.grade)}
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-slate-500 sm:text-sm">تقدمك</p>
              <span className="rounded-full bg-[#001848] px-2.5 py-0.5 text-xs font-bold text-white">
                {progress}%
              </span>
            </div>
            <div className="mb-3 h-2.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-l from-[#001848] to-gold-500 transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <button
              type="button"
              onClick={onContinue}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-gold-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-gold-400 sm:w-auto"
            >
              <PlayCircle className="h-4 w-4" />
              كمّل التعلم
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 px-4 py-4 sm:px-5">
        <button
          type="button"
          onClick={onToggleCurriculum}
          className="flex w-full min-h-11 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-right transition hover:border-gold-300 hover:bg-orange-50/40 sm:px-4"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#001848] text-white">
              <BookOpen className="h-4 w-4" />
            </span>
            <span className="text-sm font-bold text-[#001848] sm:text-base">محتوى الكورس</span>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {isExpanded && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
            {isLoadingCurriculum ? (
              <div className="flex justify-center py-10">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#001848]" />
              </div>
            ) : curriculum?.units && curriculum.units.length > 0 ? (
              <div className="space-y-2.5">
                {curriculum.units.map((unit: Unit, unitIndex: number) => {
                  const isUnitExpanded = expandedUnitIndexes.has(unitIndex);
                  const unitLessons = unit.lessons || [];

                  return (
                    <div
                      key={unit.id}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50/50"
                    >
                      <button
                        type="button"
                        onClick={() => onToggleUnit(unitIndex)}
                        className="flex w-full min-h-11 items-center gap-3 p-3 text-right transition hover:bg-white sm:p-3.5"
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                            isUnitExpanded ? 'bg-[#001848] text-white' : 'bg-white text-[#001848] border border-slate-200'
                          }`}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform duration-200 ${
                              isUnitExpanded ? 'rotate-180' : '-rotate-90'
                            }`}
                          />
                        </span>
                        <span className="flex-1 text-sm font-bold text-[#001848] sm:text-base">
                          {unit.unitName}
                        </span>
                        <span className="shrink-0 rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 sm:text-xs">
                          {unitLessons.length} درس
                        </span>
                      </button>

                      {isUnitExpanded && (
                        <div className="space-y-2 border-t border-slate-200 px-3 pb-3 pt-3 sm:px-3.5">
                          {unitLessons.map((lesson: Lesson) => {
                            const isCompleted =
                              (lesson.type === 'Video' &&
                                (lesson as Lesson & { isCompleted?: boolean }).isCompleted) ||
                              (lesson.type === 'Quiz' &&
                                (lesson as Lesson & { isQuizSubmitted?: boolean }).isQuizSubmitted) ||
                              (lesson.type === 'Assignment' &&
                                (lesson as Lesson & { isAssignmentSubmitted?: boolean })
                                  .isAssignmentSubmitted) ||
                              (lesson.type === 'File' &&
                                (lesson as Lesson & { isFileOpened?: boolean }).isFileOpened);

                            return (
                              <button
                                key={lesson.id}
                                type="button"
                                onClick={onContinue}
                                className={`flex w-full items-start gap-2.5 rounded-xl border p-3 text-right transition hover:shadow-sm ${
                                  isCompleted
                                    ? 'border-orange-200 bg-orange-50/70'
                                    : 'border-slate-200 bg-white hover:border-[#001848]/20'
                                }`}
                              >
                                <span
                                  className={`flex shrink-0 rounded-lg p-2 ${
                                    isCompleted
                                      ? 'bg-gold-500 text-white'
                                      : 'bg-slate-100 text-[#001848]'
                                  }`}
                                >
                                  {getLessonTypeIcon(lesson)}
                                </span>
                                <span
                                  className={`flex-1 text-sm font-semibold leading-relaxed ${
                                    isCompleted ? 'text-[#001848]' : 'text-slate-700'
                                  }`}
                                >
                                  {lesson.lessonName}
                                </span>
                                {isCompleted && (
                                  <CheckCircle2 className="ms-auto h-5 w-5 shrink-0 text-gold-500" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-slate-500">لا يوجد محتوى متاح حالياً</p>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default EnrolledCourseCard;
